import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getCustomers } from '../api';
import api from '../api';

const TYPE_COLORS = {
  'P': '#378ADD',
  'B': '#1D9E75',
  'L': '#D85A30',
  'Unknown': '#888780',
};
const TYPE_LABELS = {
  'P': 'Portal',
  'B': 'Background',
  'L': 'Legacy',
  'Unknown': 'Unknown',
};

export default function Sessions({ dimension, activeOnly }) {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected]   = useState('');
  const [data, setData]           = useState(null);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  useEffect(() => {
    getCustomers(activeOnly).then(r => {
      setCustomers(r.data);
      setSelected('');
      setData(null);
    });
    api.get(`/api/v1/sessions/summary?dimension=${dimension}`)
      .then(r => setSummary(r.data))
      .catch(() => setSummary(null));
  }, [activeOnly, dimension]);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/api/v1/sessions/${selected}?dimension=${dimension}`)
      .then(r => { setData(r.data); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selected, dimension]);

  const userTypes = data
    ? [...new Set(data.months.flatMap(m => Object.keys(m.by_type)))]
    : [];

  const chartData = (data?.months || []).map(m => {
    const entry = {
      month: new Date(m.log_month).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
      total: m.total,
    };
    Object.entries(m.by_type).forEach(([k, v]) => { entry[k] = v; });
    return entry;
  });

  return (
    <>
      {/* Summary metrics */}
      {summary && (
        <div className="metrics" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
          <div className="metric">
            <div className="metric-label">Total sessions (all customers)</div>
            <div className="metric-value">{summary.total_sessions.toLocaleString()}</div>
            <div className="metric-sub">{dimension === 'last_month' ? 'last month' : dimension === 'last_year' ? 'last 12 months' : 'all time'}</div>
          </div>
          <div className="metric">
            <div className="metric-label">Customers with sessions</div>
            <div className="metric-value">{summary.customer_count}</div>
          </div>
          <div className="metric">
            <div className="metric-label">Most active customer</div>
            <div className="metric-value" style={{ fontSize: 15 }}>{summary.customers?.[0]?.cust_name || '—'}</div>
            <div className="metric-sub">{summary.customers?.[0]?.total_sessions?.toLocaleString()} sessions</div>
          </div>
        </div>
      )}

      {/* Customer selector */}
      <div style={{ marginBottom: 16 }}>
        <select
          style={{ fontSize: 13, padding: '7px 12px', border: '1px solid #e5e5e5', borderRadius: 6, minWidth: 280 }}
          value={selected}
          onChange={e => setSelected(e.target.value)}
        >
          <option value="">Select a customer...</option>
          {[...customers].sort((a, b) => a.cust_name.localeCompare(b.cust_name)).map(c => (
            <option key={c.cust_code} value={c.cust_code}>{c.cust_name}</option>
          ))}
        </select>
      </div>

      {!selected && <div className="loading">Select a customer to view session history</div>}
      {loading  && <div className="loading">Loading...</div>}
      {error    && <div className="error">Error: {error}</div>}

      {data && !loading && (
        <>
          <div className="metrics" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
            <div className="metric">
              <div className="metric-label">Total sessions</div>
              <div className="metric-value">{data.total.toLocaleString()}</div>
            </div>
            <div className="metric">
              <div className="metric-label">Months of data</div>
              <div className="metric-value">{data.month_count}</div>
              <div className="metric-sub">{data.date_from} → {data.date_to}</div>
            </div>
            <div className="metric">
              <div className="metric-label">User types</div>
              <div className="metric-value">{userTypes.length}</div>
              <div className="metric-sub">{userTypes.map(t => TYPE_LABELS[t] || t).join(', ')}</div>
            </div>
          </div>

          {/* Sessions chart — stacked by user type */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">{data.customer?.cust_name} — sessions per month</div>
              <div className="legend">
                {userTypes.map(t => (
                  <span key={t}>
                    <span className="legend-dot" style={{ background: TYPE_COLORS[t] || '#888' }}></span>
                    {TYPE_LABELS[t] || t}
                  </span>
                ))}
              </div>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={(v, name) => [v.toLocaleString(), TYPE_LABELS[name] || name]} />
                  {userTypes.map((t, i) => (
                    <Bar key={t} dataKey={t} stackId="a" fill={TYPE_COLORS[t] || '#888'}
                      radius={i === userTypes.length - 1 ? [2,2,0,0] : [0,0,0,0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Monthly breakdown table */}
          <div className="card">
            <div className="card-header"><div className="card-title">Monthly breakdown</div></div>
            <table>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Total</th>
                  {userTypes.map(t => <th key={t}>{TYPE_LABELS[t] || t}</th>)}
                </tr>
              </thead>
              <tbody>
                {[...data.months].reverse().map((m, i) => (
                  <tr key={i}>
                    <td>{new Date(m.log_month).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</td>
                    <td style={{ fontWeight: 500 }}>{m.total.toLocaleString()}</td>
                    {userTypes.map(t => (
                      <td key={t} style={{ color: '#666' }}>{(m.by_type[t] || 0).toLocaleString()}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* All customers summary table */}
          {summary && (
            <div className="card">
              <div className="card-header"><div className="card-title">All customers — session summary</div></div>
              <table>
                <thead>
                  <tr><th>Customer</th><th>Location</th><th>Sessions</th></tr>
                </thead>
                <tbody>
                  {summary.customers.map(c => (
                    <tr key={c.cust_code}>
                      <td style={{ fontWeight: 500 }}>{c.cust_name}</td>
                      <td style={{ color: '#666' }}>{c.location}</td>
                      <td>{(c.total_sessions || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}
